import React, {
  useMemo,
  type HTMLAttributes,
  type MutableRefObject,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';

import { RecursiveMenu } from '@uncinc/react-drupal-core';
import { Container, Lottie, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper } from '../../../../components';
import { SCREEN_MD } from '../../../../config/constants';
import SoloSearch from '../../../../components/Search/SoloSearch';

import hamburgerLottie from '../../../../assets/lotties/hamburger.json';
import hamburgerSearchLottie from '../../../../assets/lotties/hamburger-search.json';
import Logo from '../../Logo';

import './style.scss';

interface MenuButtonProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  isScreenMedium: boolean;
  lottieRef: RefObject<HTMLDivElement>;
  isOverlayOpen: MutableRefObject<boolean>;
}

const MenuButton: React.FC<MenuButtonProps> = React.memo(({
  isScreenMedium,
  lottieRef,
  isOverlayOpen,
}) => (
  <Lottie
    // @ts-ignore
    id="hamburgerOpen"
    ref={lottieRef}
    data={isScreenMedium ? hamburgerLottie : hamburgerSearchLottie}
    autoPlay={false}
    loop={false}
    direction={1}
    onInit={(instance: any) => {
      instance.addEventListener('complete', () => {
        instance.setDirection(isOverlayOpen.current ? -1 : 1);
      });
    }}
  />
));

interface HeaderProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  isOverlayOpen: MutableRefObject<boolean>;
  toggleOverlay: (arg0?: boolean) => void;
  headerRef: RefObject<HTMLDivElement>;
  lottieRef: RefObject<HTMLDivElement>;
}

const Header: React.FC<HeaderProps> = ({
  isOverlayOpen,
  toggleOverlay,
  headerRef,
  lottieRef,
}) => {
  const { t } = useTranslation();

  const { width } = useWindowDimensions();

  const isScreenMedium = useMemo(() => width > SCREEN_MD, [width]);

  return (
    <div className="HeaderMenu__Header">
      <div className="HeaderMenu__Header__background">
        <Container className="HeaderMenu__Header__wrapper">
          <Logo toggleOverlay={toggleOverlay} />

          <div className="HeaderMenu__Header__actions">
            <div
              ref={headerRef}
              className="HeaderMenu__Header__menu show"
            >
              <RecursiveMenu
                menuId="main"
                maxLevel={1}
                onClickFunction={() => toggleOverlay(true)}
              />
            </div>
            <div className="HeaderMenu__Header__menu-actions">
              <RecursiveMenu
                menuId="navigation-actions"
                maxLevel={1}
                onClickFunction={() => toggleOverlay(true)}
              />
            </div>

            {isScreenMedium && (
              <SoloSearch collapsible />
            )}

            <ButtonWrapper
              className="HeaderMenu__Header__hamburger"
              tabIndex={0}
              type="textual"
              onClick={() => toggleOverlay()}
            >
              <div className="HeaderMenu__Header__hamburger__label">
                <span className="HeaderMenu__Header__hamburger__label__state">{t('general.close')}</span>
                <span className="HeaderMenu__Header__hamburger__label__state">{t('general.menu')}</span>
              </div>
              <div className="HeaderMenu__Header__hamburger__button">
                <MenuButton
                  isScreenMedium={isScreenMedium}
                  lottieRef={lottieRef}
                  isOverlayOpen={isOverlayOpen}
                />
              </div>
            </ButtonWrapper>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default Header;
